import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { useTranslation } from "@coworker/locales";
import { useDeleteTeam, useUpdateTeam } from "../../hooks/useTeamMutations";
import TeamModal from "./TeamModal";
import { NewTeamDataType } from "../../types/TeamMutationTypes";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { useState } from "react";
import DeleteTeamConfirmationPrompt from "./DeleteTeamConfirmationPrompt";

interface EditTeamModalProps {
  visible: boolean;
  close: () => void;
  storeId: string;
  hasActiveProductQualityTeam: boolean;
  selectedTeam: StoreTeam;
  resetFilters: () => void;
  onDelete: () => void;
}

const EditTeamModal = ({
  visible,
  close,
  storeId,
  hasActiveProductQualityTeam,
  selectedTeam,
  resetFilters,
  onDelete,
}: EditTeamModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastNotification();
  const { mutateAsync: updateTeam, isPending: isSubmitting } =
    useUpdateTeam(storeId);
  const { mutateAsync: deleteTeam, isPending: isDeleting } =
    useDeleteTeam(storeId);

  const handleSubmit = async (newTeamData: NewTeamDataType) => {
    try {
      await updateTeam(newTeamData);
      showToast(
        t("teamSuccessfullyUpdatedString", { teamName: newTeamData.name })
      );
      close();
    } catch (error) {
      showToast(t("failedUpdatingTeamString", { teamName: newTeamData.name }));
      console.error("Error updating team:", error);
    }
  };

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteTeam(selectedTeam.id);
      showToast(
        t("teamSuccessfullyDeletedString", { teamName: selectedTeam.name })
      );
      setShowDeleteConfirmation(false);
      close();
      resetFilters();
      onDelete();
    } catch (error) {
      showToast(t("failedDeletingTeamString", { teamName: selectedTeam.name }));
      setShowDeleteConfirmation(false);
      console.error("Error deleting team:", error);
    }
  };

  return (
    <>
      <TeamModal
        visible={visible}
        close={close}
        storeId={storeId}
        hasActiveProductQualityTeam={hasActiveProductQualityTeam}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        initialData={selectedTeam}
        handleDelete={() => setShowDeleteConfirmation(true)}
      />
      <DeleteTeamConfirmationPrompt
        showDeleteConfirmation={showDeleteConfirmation}
        setShowDeleteConfirmation={setShowDeleteConfirmation}
        isDeleting={isDeleting}
        handleDelete={handleDelete}
        selectedTeam={selectedTeam}
        t={t}
      />
    </>
  );
};

export default EditTeamModal;
